import { createClient } from '@supabase/supabase-js';

// Replace these with your actual Supabase URL and anon key
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

export const supabase = createClient(supabaseUrl, supabaseKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true
  },
  realtime: {
    params: {
      eventsPerSecond: 10
    }
  },
  global: {
    headers: {
      'x-my-custom-header': 'krm-madhubani'
    }
  }
});

// Auth functions
export const signIn = async (email, password) => {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });
  return { data, error };
};

export const signOut = async () => {
  const { error } = await supabase.auth.signOut();
  return { error };
};

export const getCurrentUser = () => {
  return supabase.auth.getUser();
};

// Student-related functions
export const fetchStudents = async () => {
  try {
    // Fetch data from main students table
    const { data: studentsData, error: studentsError } = await supabase
      .from('students')
      .select('*');
      
    if (studentsError) {
      console.error('Error fetching students:', studentsError);
      throw studentsError;
    }
    
    // Fetch data from private details table
    const { data: privateData, error: privateError } = await supabase
      .from('students_private_details')
      .select('*');
      
    if (privateError) {
      console.error('Error fetching private student details:', privateError);
      throw privateError;
    }
    
    // Create a map for quick lookup of private data
    const privateDataMap = {};
    privateData.forEach(item => {
      privateDataMap[item.Rno] = item;
    });
    
    // Merge data from both tables
    const mergedData = studentsData.map(student => ({
      ...student,
      ...privateDataMap[student.Rno] || {}
    }));
    
    return mergedData;
  } catch (error) {
    console.error('Error in fetchStudents:', error);
    throw error;
  }
};

// Fetch students by class
export const fetchStudentsByClass = async (classValue) => {
  try {
    // Fetch filtered students
    const { data: studentsData, error: studentsError } = await supabase
      .from('students')
      .select('*')
      .eq('Class', classValue)
      .order('Rno', { ascending: true });
      
    if (studentsError) {
      console.error('Error fetching students by class:', studentsError);
      throw studentsError;
    }
    
    // Extract the Rno values to get private details
    const studentRnos = studentsData.map(student => student.Rno);
    
    // If no students found, return empty array
    if (studentRnos.length === 0) return [];
    
    // Fetch private details for these students
    const { data: privateData, error: privateError } = await supabase
      .from('students_private_details')
      .select('*')
      .in('Rno', studentRnos);
      
    if (privateError) {
      console.error('Error fetching private student details:', privateError);
      throw privateError;
    }
    
    // Create a map for quick lookup
    const privateDataMap = {};
    privateData.forEach(item => {
      privateDataMap[item.Rno] = item;
    });
    
    // Merge data from both tables
    const mergedData = studentsData.map(student => ({
      ...student,
      ...privateDataMap[student.Rno] || {}
    }));
    
    return mergedData;
  } catch (error) {
    console.error('Error in fetchStudentsByClass:', error);
    throw error;
  }
};

export const addStudent = async (studentData) => {
  try {
    // Split data for the two tables
    const mainTableFields = ['Rno', 'Name', 'Gender', 'Class', 'Active', 'Transport', 'Transport Fee'];
    const privateTableFields = [
      'Rno', 'Pen No', 'Aadhar Name', 'DOB', 'Mobile1', 'Mobile2', 
      'Aadhar No', 'Religion', 'Category', 'Caste', 
      'Father\'s Name (Son)', 'Father\'s Name (Father)', 
      'Mother\'s Name (Son)', 'Mother\'s Name (Mother)', 
      'Address Temporary', 'Address Permanent', 
      'Date of Admission', 'Comment', 'Comment2', 'FeeDues', 'Default Discount'
    ];
    
    const mainStudentData = {};
    const privateStudentData = { Rno: studentData.Rno };
    
    // Separate data for each table
    Object.entries(studentData).forEach(([key, value]) => {
      if (mainTableFields.includes(key)) {
        mainStudentData[key] = value;
      }
      if (privateTableFields.includes(key) && key !== 'Rno') { // Rno already added
        privateStudentData[key] = value;
      }
    });
    
    // Begin transaction
    // First insert into main students table
    const { data: mainData, error: mainError } = await supabase
      .from('students')
      .insert([mainStudentData])
      .select();
      
    if (mainError) {
      console.error('Error adding student to main table:', mainError);
      throw mainError;
    }
    
    // Then insert into private details table
    const { data: privateData, error: privateError } = await supabase
      .from('students_private_details')
      .insert([privateStudentData])
      .select();
      
    if (privateError) {
      // If private data insert fails, we should ideally delete the main entry
      // but since Supabase doesn't support true transactions, we'll just report the error
      console.error('Error adding student private details:', privateError);
      throw privateError;
    }
    
    // Return merged data
    return { ...mainData[0], ...privateData[0] };
  } catch (error) {
    console.error('Error in addStudent:', error);
    throw error;
  }
};

// Fetch attendance records for a specific class, month, and year
export const fetchAttendance = async (classValue, month, year) => {
  try {
    // First, get all students in the specified class
    const { data: students, error: studentsError } = await supabase
      .from('students')
      .select('Rno, Name, Class')
      .eq('Class', classValue);
      
    if (studentsError) {
      console.error('Error fetching students:', studentsError);
      throw studentsError;
    }
    
    if (!students || students.length === 0) {
      return [];
    }
    
    // Get student Rnos
    const studentRnos = students.map(student => student.Rno);
    
    // Fetch attendance records for these students in the specified month and year
    const { data: attendanceRecords, error: attendanceError } = await supabase
      .from('attendance')
      .select('*')
      .in('Rno', studentRnos)
      .eq('month', month)
      .eq('year', year);
      
    if (attendanceError) {
      console.error('Error fetching attendance records:', attendanceError);
      throw attendanceError;
    }
    
    // Create a map of student attendance for easy lookup
    const attendanceMap = new Map();
    attendanceRecords.forEach(record => {
      attendanceMap.set(record.Rno, record);
    });
    // Combine student data with attendance records
    const result = {};
    
    students.forEach(student => {
      const attendanceRecord = attendanceMap.get(student.Rno);
      const studentAttendance = {};
      
      // Process each day (01-31)
      for (let day = 1; day <= 31; day++) {
        const dayKey = day < 10 ? `0${day}` : `${day}`;
        studentAttendance[day] = attendanceRecord ? attendanceRecord[dayKey] : false;
      }
      
      result[student.Rno] = studentAttendance;
    });
    return result;
  } catch (error) {
    console.error('Error in fetchAttendance:', error);
    throw error;
  }
};

// Helper to get ISO string in IST timezone
function getISTISOString() {
  const now = new Date();
  // India is UTC+5:30 = 330 minutes
  const offsetMinutes = 330;
  const ist = new Date(now.getTime() + (offsetMinutes - now.getTimezoneOffset()) * 60000);
  return ist.toISOString().replace('Z', '+05:30');
}

// Update attendance records in bulk (all students at once)
export const updateAttendanceBulk = async (classValue, month, year, attendanceData) => {
  try {
    // Get current user
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;
    
    if (!user) {
      throw new Error('No user is currently logged in');
    }
    
    // Process all students' attendance at once
    const results = [];
    const studentIds = Object.keys(attendanceData);

    // First check which records already exist
    const { data: existingRecords, error: fetchError } = await supabase
      .from('attendance')
      .select('Rno')
      .in('Rno', studentIds.map(id => parseInt(id)))
      .eq('month', month)
      .eq('year', year);
      
    if (fetchError) {
      console.error('Error checking existing attendance records:', fetchError);
      throw fetchError;
    }
    
    // Create a map of existing records for quick lookup
    const existingMap = new Map();
    if (existingRecords && existingRecords.length > 0) {
      existingRecords.forEach(record => {
        existingMap.set(record.Rno.toString(), true);
      });
    }
    
    // Process each student
    for (const [studentId, attendance] of Object.entries(attendanceData)) {
      const studentIdInt = parseInt(studentId);
      const record = {
        Rno: studentIdInt,
        month,
        year,
        updated_by: user.id,
        updated_at: getISTISOString()
      };
      
      // Add day columns (01-31)
      for (let day = 1; day <= 31; day++) {
        const dayKey = day < 10 ? `0${day}` : `${day}`;
        if (attendance[day] !== undefined) {
          record[dayKey] = attendance[day];
        }
      }
      
      let result;
      
      // If record exists, update it. Otherwise insert it.
      if (existingMap.has(studentId)) {
        // Update existing record
        const { data, error } = await supabase
          .from('attendance')
          .update(record)
          .eq('Rno', studentIdInt)
          .eq('month', month)
          .eq('year', year);
          
        if (error) {
          console.error(`Error updating attendance record for student ${studentId}:`, error);
          throw error;
        }
        
        result = data;
      } else {
        // Insert new record
        const { data, error } = await supabase
          .from('attendance')
          .insert(record);
          
        if (error) {
          console.error(`Error inserting attendance record for student ${studentId}:`, error);
          throw error;
        }
        
        result = data;
      }
      
      results.push(result);
    }
    
    return results;
  } catch (error) {
    console.error('Error in updateAttendanceBulk:', error);
    throw error;
  }
};

// Update staff attendance records in bulk (all staffs at once)
export const updateStaffAttendanceBulk = async (month, year, attendanceData) => {
  try {
    // Get current user
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;
    
    if (!user) {
      throw new Error('No user is currently logged in');
    }
    
    // Process all staffs' attendance at once
    const results = [];
    const staffIds = Object.keys(attendanceData);

    // First check which records already exist
    const { data: existingRecords, error: fetchError } = await supabase
      .from('attendance')
      .select('Tno')
      .in('Tno', staffIds)
      .eq('month', month)
      .eq('year', year);
      
    if (fetchError) {
      console.error('Error checking existing staff attendance records:', fetchError);
      throw fetchError;
    }
    
    // Create a map of existing records for quick lookup
    const existingMap = new Map();
    if (existingRecords && existingRecords.length > 0) {
      existingRecords.forEach(record => {
        existingMap.set(record.Tno.toString(), true);
      });
    }
    
    // Process each staff
    for (const [staffId, attendance] of Object.entries(attendanceData)) {
      const record = {
        Tno: staffId,
        month,
        year,
        updated_by: user.id,
        updated_at: getISTISOString()
      };
      
      // Add day columns (01-31)
      for (let day = 1; day <= 31; day++) {
        const dayKey = day < 10 ? `0${day}` : `${day}`;
        if (attendance[day] !== undefined) {
          record[dayKey] = attendance[day];
        }
      }
      
      let result;
      
      // If record exists, update it. Otherwise insert it.
      if (existingMap.has(staffId)) {
        // Update existing record
        const { data, error } = await supabase
          .from('attendance')
          .update(record)
          .eq('Tno', staffId)
          .eq('month', month)
          .eq('year', year);
          
        if (error) {
          console.error(`Error updating attendance record for staff ${staffId}:`, error);
          throw error;
        }
        
        result = data;
      } else {
        // Insert new record
        const { data, error } = await supabase
          .from('attendance')
          .insert(record);
          
        if (error) {
          console.error(`Error inserting attendance record for staff ${staffId}:`, error);
          throw error;
        }
        
        result = data;
      }
      
      results.push(result);
    }
    
    return results;
  } catch (error) {
    console.error('Error in updateStaffAttendanceBulk:', error);
    throw error;
  }
};

// Class-related functions
export const fetchClasses = async () => {
  try {
    const { data, error } = await supabase
      .from('classes')
      .select(`
        *,
        staff:staffs!class_teacher(name)
      `)
      .order('class_name', { ascending: true });
      
    if (error) {
      console.error('Error fetching classes:', error);
      throw error;
    }
    
    // Transform data to include staff name in class_teacher field
    const transformedData = data?.map(cls => ({
      ...cls,
      class_teacher_name: cls.staff?.name || 'Not Assigned',
      class_teacher: cls.class_teacher // Keep original ID for editing
    })) || [];
    
    return transformedData;
  } catch (error) {
    console.error('Error in fetchClasses:', error);
    throw error;
  }
};

export const addClass = async (classData) => {
  try {
    const { data, error } = await supabase
      .from('classes')
      .insert([classData])
      .select(`
        *,
        staff:staffs!class_teacher(name)
      `);
      
    if (error) {
      console.error('Error adding class:', error);
      throw error;
    }
    
    if (!data || data.length === 0) {
      return null;
    }
    
    // Transform data to include staff name
    const newClass = {
      ...data[0],
      class_teacher_name: data[0].staff?.name || 'Not Assigned',
      class_teacher: data[0].class_teacher // Keep original ID
    };
    
    return newClass;
  } catch (error) {
    console.error('Error in addClass:', error);
    throw error;
  }
};

export const updateClass = async (id, classData) => {
  try {
    // Remove join aliases and computed fields that don't exist in the classes table
    const { staff, teacher, class_teacher_name, ...cleanData } = classData;

    const { data, error } = await supabase
      .from('classes')
      .update(cleanData)
      .eq('id', id)
      .select(`
        *,
        staff:staffs!class_teacher(name)
      `);
      
    if (error) {
      console.error('Error updating class:', error);
      throw error;
    }
    
    if (!data || data.length === 0) {
      return null;
    }
    
    // Transform data to include staff name
    const updatedClass = {
      ...data[0],
      class_teacher_name: data[0].staff?.name || 'Not Assigned',
      class_teacher: data[0].class_teacher // Keep original ID
    };
    
    return updatedClass;
  } catch (error) {
    console.error('Error in updateClass:', error);
    throw error;
  }
};

export const deleteClass = async (id) => {
  try {
    const { error } = await supabase
      .from('classes')
      .delete()
      .eq('id', id);
      
    if (error) {
      console.error('Error deleting class:', error);
      throw error;
    }
    
    return true;
  } catch (error) {
    console.error('Error in deleteClass:', error);
    throw error;
  }
};

// Get student counts by class (active and inactive)
export const fetchStudentCountsByClass = async () => {
  try {
    // Query to get counts of active and inactive students for each class
    // Fetch all students' class and active status
    const { data, error } = await supabase
      .from('students')
      .select('Class, Active');

    if (error) {
      console.error('Error fetching student data:', error);
      throw error;
    }

    // Aggregate counts in JS
    const countsByClass = {};
    data.forEach(item => {
      if (!countsByClass[item.Class]) {
        countsByClass[item.Class] = { active: 0, inactive: 0 };
      }
      if (item.Active) {
        countsByClass[item.Class].active += 1;
      } else {
        countsByClass[item.Class].inactive += 1;
      }
    });

    return countsByClass;
  } catch (error) {
    console.error('Error in fetchStudentCountsByClass:', error);
    throw error;
  }
};

// Fetch staff attendance records for a specific month and year
export const fetchTeacherAttendance = async (month, year) => {
  try {
    // Fetch all staffs first
    const { data: staffs, error: staffError } = await supabase
      .from('staffs')
      .select('staff_no')
      .eq('is_active', true);
      
    if (staffError) {
      console.error('Error fetching staffs:', staffError);
      throw staffError;
    }
    
    if (!staffs || staffs.length === 0) {
      return {};
    }
    
    // Get staff numbers
    const staffNos = staffs.map(s => s.staff_no);
    
    // Fetch attendance records for these staffs in the specified month and year
    const { data: attendanceRecords, error: attendanceError } = await supabase
      .from('attendance')
      .select('*')
      .in('Tno', staffNos)
      .eq('month', month)
      .eq('year', year);
      
    if (attendanceError) {
      console.error('Error fetching staff attendance records:', attendanceError);
      throw attendanceError;
    }
    
    // Create a map of staff attendance for easy lookup
    const attendanceMap = new Map();
    attendanceRecords.forEach(record => {
      attendanceMap.set(record.Tno, record);
    });
    
    // Combine staff data with attendance records
    const result = {};
    
    staffs.forEach(staff => {
      const attendanceRecord = attendanceMap.get(staff.staff_no);
      const staffAttendance = {};
      
      // Process each day (01-31)
      for (let day = 1; day <= 31; day++) {
        const dayKey = day < 10 ? `0${day}` : `${day}`;
        staffAttendance[day] = attendanceRecord ? attendanceRecord[dayKey] : false;
      }
      
      result[staff.staff_no] = staffAttendance;
    });
    return result;
  } catch (error) {
    console.error('Error in fetchTeacherAttendance:', error);
    throw error;
  }
};

// Fetch all active staffs
export const fetchStaffs = async () => {
  try {
    const { data, error } = await supabase
      .from('staffs')
      .select('*')
      .order('staff_no', { ascending: true });
      
    if (error) {
      console.error('Error fetching staffs:', error);
      throw error;
    }
    
    return data;
  } catch (error) {
    console.error('Error in fetchStaffs:', error);
    throw error;
  }
};

// Add a new staff to the database
export const addStaff = async (staffData) => {
  try {
    // Generate a unique staff_no if not provided
    if (!staffData.staff_no) {
      const timestamp = new Date().getTime();
      staffData.staff_no = `S${timestamp.toString().slice(-6)}`;
    }

    // Format the data according to the database schema
    const formattedData = {
      staff_no: staffData.staff_no,
      name: staffData.name,
      role: staffData.role || 'Teacher',
      is_active: true
    };

    // Map standard fields with appropriate type conversions
    if (staffData.gender !== undefined) formattedData.gender = staffData.gender || null;
    if (staffData.dob !== undefined) formattedData.dob = staffData.dob || null;
    if (staffData.phone !== undefined) formattedData.mobile = staffData.phone ? parseInt(staffData.phone, 10) : null;
    if (staffData.email !== undefined) formattedData.email = staffData.email || null;
    if (staffData.address !== undefined) formattedData.address = staffData.address || null;
    if (staffData.qualification !== undefined) formattedData.qualification = staffData.qualification || null;
    if (staffData.subject !== undefined) formattedData.subject_specialization = staffData.subject || null;
    if (staffData.dateOfJoining !== undefined) formattedData.date_of_joining = staffData.dateOfJoining || null;
    if (staffData.aadhar !== undefined) formattedData.aadhaar_no = staffData.aadhar ? parseInt(staffData.aadhar, 10) : null;
    if (staffData.fathersAadhar !== undefined) formattedData.fathers_aadhaar = staffData.fathersAadhar ? parseInt(staffData.fathersAadhar, 10) : null;
    if (staffData.mothersAadhar !== undefined) formattedData.mothers_aadhaar = staffData.mothersAadhar ? parseInt(staffData.mothersAadhar, 10) : null;
    if (staffData.salary !== undefined) formattedData.salary = staffData.salary ? parseInt(staffData.salary, 10) : null;
    
    // Handle any additional fields that might be in the form
    Object.keys(staffData).forEach(key => {
      // Skip fields we've already processed and form-specific fields
      const processedFields = ['staff_no', 'teacher_no', 'name', 'gender', 'dob', 'phone', 'email', 'address', 
                              'qualification', 'subject', 'dateOfJoining', 'aadhar', 'fathersAadhar', 'mothersAadhar', 'salary', 'is_active', 'role'];
      
      if (!processedFields.includes(key)) {
        const value = staffData[key];
        
        // Handle empty strings - convert to null to avoid bigint errors
        if (value === '' || value === undefined) {
          formattedData[key] = null;
        }
        // Check if the value is a numeric string and convert it to a number
        else if (typeof value === 'string' && !isNaN(value) && value.trim() !== '') {
          formattedData[key] = Number(value.trim());
        }
        // Otherwise, use the value as-is
        else {
          formattedData[key] = value;
        }
      }
    });

    const { data, error } = await supabase
      .from('staffs')
      .insert(formattedData)
      .select();

    if (error) {
      console.error('Error adding staff:', error);
      throw error;
    }

    return data[0];
  } catch (error) {
    console.error('Error in addStaff:', error);
    throw error;
  }
};

// Update an existing staff
export const updateStaff = async (staffId, staffData) => {
  try {
    console.log('updateStaff called with ID:', staffId);
    
    if (!staffId) {
      throw new Error('Staff ID is required for updating a staff');
    }
    
    // Remove id field if present as it doesn't exist in the database schema
    if (staffData.id) {
      console.log('Removing id field from staffData as it does not exist in schema');
      delete staffData.id;
    }
    
    // Format the data according to the database schema
    const formattedData = {};
    
    // Map standard fields with appropriate type conversions
    if (staffData.name !== undefined) formattedData.name = staffData.name;
    if (staffData.gender !== undefined) formattedData.gender = staffData.gender || null;
    if (staffData.dob !== undefined) formattedData.dob = staffData.dob || null;
    
    // Handle phone/mobile - convert to number or null (not empty string)
    if (staffData.phone !== undefined) {
      formattedData.mobile = staffData.phone && staffData.phone.trim() !== '' 
        ? Number(staffData.phone.trim()) 
        : null;
    }
    
    if (staffData.email !== undefined) formattedData.email = staffData.email || null;
    if (staffData.address !== undefined) formattedData.address = staffData.address || null;
    if (staffData.qualification !== undefined) formattedData.qualification = staffData.qualification || null;
    if (staffData.subject !== undefined) formattedData.subject_specialization = staffData.subject || null;
    if (staffData.dateOfJoining !== undefined) formattedData.date_of_joining = staffData.dateOfJoining || null;
    if (staffData.is_active !== undefined) formattedData.is_active = staffData.is_active;
    if (staffData.role !== undefined) formattedData.role = staffData.role || null;
    
    // Handle aadhar - convert to number or null (not empty string)
    if (staffData.aadhar !== undefined) {
      formattedData.aadhaar_no = staffData.aadhar && staffData.aadhar.trim() !== '' 
        ? Number(staffData.aadhar.trim()) 
        : null;
    }
    if (staffData.fathersAadhar !== undefined) {
      formattedData.fathers_aadhaar = staffData.fathersAadhar && staffData.fathersAadhar.trim() !== ''
        ? Number(staffData.fathersAadhar.trim())
        : null;
    }
    if (staffData.mothersAadhar !== undefined) {
      formattedData.mothers_aadhaar = staffData.mothersAadhar && staffData.mothersAadhar.trim() !== ''
        ? Number(staffData.mothersAadhar.trim())
        : null;
    }
    if (staffData.salary !== undefined) {
      formattedData.salary = staffData.salary && staffData.salary.toString().trim() !== ''
        ? parseInt(staffData.salary, 10)
        : null;
    }
    
    // Handle any additional fields that might be in the form
    Object.keys(staffData).forEach(key => {
      // Skip fields we've already processed and form-specific fields
      const processedFields = ['staff_no', 'teacher_no', 'name', 'gender', 'dob', 'phone', 'email', 'address', 
                               'qualification', 'subject', 'dateOfJoining', 'aadhar', 'fathersAadhar', 'mothersAadhar', 'salary', 'is_active', 'role'];
      
      if (!processedFields.includes(key)) {
        const value = staffData[key];
        
        // Handle empty strings - convert to null to avoid bigint errors
        if (value === '' || value === undefined) {
          formattedData[key] = null;
        }
        // Check if the value is a numeric string and convert it to a number
        else if (typeof value === 'string' && !isNaN(value) && value.trim() !== '') {
          formattedData[key] = Number(value.trim());
        }
        // Otherwise, use the value as-is
        else {
          formattedData[key] = value;
        }
      }
    });
    
    // Add updated_at timestamp
    formattedData.updated_at = new Date().toISOString();
    
    console.log('Formatted data for update:', formattedData);
    console.log('Updating staff with ID:', staffId);

    const { data, error } = await supabase
      .from('staffs')
      .update(formattedData)
      .eq('staff_no', staffId)
      .select();

    if (error) {
      console.error('Supabase error updating staff:', error);
      throw new Error(`Database error: ${error.message}`);
    }
    
    if (!data || data.length === 0) {
      console.error('No data returned from update operation');
      throw new Error('Staff not found or no changes were made');
    }
    
    console.log('Staff updated successfully:', data[0]);
    return data[0];
  } catch (error) {
    console.error('Error in updateStaff:', error);
    throw error;
  }
};

// Delete a staff from the database
export const deleteStaff = async (staffId) => {
  try {
    const { data, error } = await supabase
      .from('staffs')
      .delete()
      .eq('staff_no', staffId)
      .select();

    if (error) {
      console.error('Error deleting staff:', error);
      throw error;
    }

    return data[0];
  } catch (error) {
    console.error('Error in deleteStaff:', error);
    throw error;
  }
};

export const updateStudent = async (Rno, studentData) => {
  try {
    // Split data for the two tables
    const mainTableFields = ['Name', 'Gender', 'Class', 'Active', 'Transport', 'Transport Fee'];
    const privateTableFields = [
      'Pen No', 'Aadhar Name', 'DOB', 'Mobile1', 'Mobile2', 
      'Aadhar No', 'Religion', 'Category', 'Caste', 
      'Father\'s Name (Son)', 'Father\'s Name (Father)', 
      'Mother\'s Name (Son)', 'Mother\'s Name (Mother)', 
      'Address Temporary', 'Address Permanent', 
      'Date of Admission', 'Comment', 'Comment2', 'FeeDues', 'Default Discount'
    ];
    
    const mainStudentData = {};
    const privateStudentData = {};
    
    // Separate data for each table
    Object.entries(studentData).forEach(([key, value]) => {
      if (mainTableFields.includes(key)) {
        mainStudentData[key] = value;
      }
      if (privateTableFields.includes(key)) {
        privateStudentData[key] = value;
      }
    });
    
    // Update main table if needed
    let mainData = null;
    if (Object.keys(mainStudentData).length > 0) {
      const { data, error } = await supabase
        .from('students')
        .update(mainStudentData)
        .eq('Rno', Rno)
        .select();
        
      if (error) {
        console.error('Error updating student main details:', error);
        throw error;
      }
      mainData = data[0];
    } else {
      // If no main data to update, fetch current data
      const { data, error } = await supabase
        .from('students')
        .select('*')
        .eq('Rno', Rno)
        .single();
        
      if (error) {
        console.error('Error fetching current student data:', error);
        throw error;
      }
      mainData = data;
    }
    
    // Update private table if needed
    let privateData = null;
    if (Object.keys(privateStudentData).length > 0) {
      const { data, error } = await supabase
        .from('students_private_details')
        .update(privateStudentData)
        .eq('Rno', Rno)
        .select();
        
      if (error) {
        console.error('Error updating student private details:', error);
        throw error;
      }
      privateData = data[0];
    } else {
      // If no private data to update, fetch current data
      const { data, error } = await supabase
        .from('students_private_details')
        .select('*')
        .eq('Rno', Rno)
        .single();
        
      if (error && error.code !== 'PGRST116') { // PGRST116 is 'no rows returned'
        console.error('Error fetching current student private data:', error);
        throw error;
      }
      privateData = data || {};
    }
    
    // Return merged data
    return { ...mainData, ...privateData };
  } catch (error) {
    console.error('Error in updateStudent:', error);
    throw error;
  }
};

// ============================================
// DASHBOARD API FUNCTIONS
// ============================================

export const fetchDashboardFinancialStats = async (activeFilter = 'all') => {
  try {
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    
    // Fetch all transactions for this month
    const { data: monthTransactions, error: monthError } = await supabase
      .from('payment_transactions')
      .select('*')
      .gte('created_at', firstDayOfMonth.toISOString());
    
    if (monthError) throw monthError;
    
    // Fetch today's transactions
    const { data: todayTransactions, error: todayError } = await supabase
      .from('payment_transactions')
      .select('*')
      .gte('created_at', todayStart.toISOString());
    
    if (todayError) throw todayError;

    // Fetch student active status to filter transactions
    let activeRnoSet = null;
    if (activeFilter !== 'all') {
      const { data: activeStudents, error: activeError } = await supabase
        .from('students')
        .select('Rno, Active');
      if (activeError) throw activeError;
      const filtered = activeStudents.filter(s =>
        activeFilter === 'active' ? s.Active !== false : s.Active === false
      );
      activeRnoSet = new Set(filtered.map(s => s.Rno));
    }

    const filteredMonthTransactions = activeRnoSet
      ? monthTransactions.filter(t => activeRnoSet.has(t.student_rno))
      : monthTransactions;

    const filteredTodayTransactions = activeRnoSet
      ? todayTransactions.filter(t => activeRnoSet.has(t.student_rno))
      : todayTransactions;
    
    // Fetch students with dues (FeeDues < 0) filtered by active status
    let duesQuery = supabase
      .from('students_private_details')
      .select('FeeDues, students!inner (Active)')
      .lt('FeeDues', 0);
    
    if (activeFilter === 'active') duesQuery = duesQuery.eq('students.Active', true);
    else if (activeFilter === 'inactive') duesQuery = duesQuery.eq('students.Active', false);
    
    const { data: studentsData, error: studentsError } = await duesQuery;
    
    if (studentsError) throw studentsError;

    // Fetch students with advance payments (FeeDues > 0) filtered by active status
    let advanceQuery = supabase
      .from('students_private_details')
      .select('FeeDues, students!inner (Active)')
      .gt('FeeDues', 0);
    
    if (activeFilter === 'active') advanceQuery = advanceQuery.eq('students.Active', true);
    else if (activeFilter === 'inactive') advanceQuery = advanceQuery.eq('students.Active', false);
    
    const { data: advanceData, error: advanceError } = await advanceQuery;
    
    if (advanceError) throw advanceError;
    
    // Calculate total revenue this month
    const totalRevenue = filteredMonthTransactions.reduce((sum, t) => sum + (parseFloat(t.amount_paid) || 0), 0);
    
    // Calculate today's collection
    const todaysCollection = filteredTodayTransactions.reduce((sum, t) => sum + (parseFloat(t.amount_paid) || 0), 0);
    
    // Calculate outstanding dues (negative dues = money owed, so take absolute value)
    const outstandingDues = studentsData.reduce((sum, s) => {
      const dues = parseFloat(s.FeeDues) || 0;
      return sum + Math.abs(dues);
    }, 0);

    // Calculate advance payments (positive dues = advance)
    const advancePayments = advanceData.reduce((sum, s) => {
      return sum + (parseFloat(s.FeeDues) || 0);
    }, 0);
    
    // Calculate payment method distribution
    const paymentMethods = filteredMonthTransactions.reduce((acc, t) => {
      const method = t.payment_method || 'Cash';
      acc[method] = (acc[method] || 0) + parseFloat(t.amount_paid || 0);
      return acc;
    }, {});
    
    // Calculate total discount for the month (sum of all discounts from transactions)
    const totalDiscount = filteredMonthTransactions.reduce((sum, t) => {
      return sum + Math.abs(parseFloat(t.discount) || 0);
    }, 0);
    
    return {
      totalRevenue,
      todaysCollection,
      outstandingDues,
      advancePayments,
      totalTransactions: filteredTodayTransactions.length,
      totalDiscount,
      paymentMethods,
      transactions: filteredMonthTransactions
    };
  } catch (error) {
    console.error('Error fetching financial stats:', error);
    throw error;
  }
};

export const fetchRevenueHistory = async () => {
  try {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth() + 1; // 1-12
    
    // Academic session: March to March
    // If current month is Jan, Feb, or March (1-3), session started last year
    // Otherwise, session started this year
    let sessionStartYear = currentMonth >= 3 ? currentYear : currentYear - 1;
    
    // Session starts from March of sessionStartYear
    const startDate = new Date(sessionStartYear, 2, 1); // Month 2 = March (0-indexed)
    
    const { data, error } = await supabase
      .from('payment_transactions')
      .select('created_at, amount_paid')
      .gte('created_at', startDate.toISOString())
      .order('created_at', { ascending: true });
    
    if (error) throw error;
    
    // Group by month
    const monthlyRevenue = {};
    data.forEach(transaction => {
      const date = new Date(transaction.created_at);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      monthlyRevenue[monthKey] = (monthlyRevenue[monthKey] || 0) + parseFloat(transaction.amount_paid || 0);
    });
    
    return monthlyRevenue;
  } catch (error) {
    console.error('Error fetching revenue history:', error);
    throw error;
  }
};

export const fetchReconciliationHistory = async () => {
  try {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth() + 1; // 1-12
    
    // Academic session: March to March
    // If current month is Jan, Feb, or March (1-3), session started last year
    // Otherwise, session started this year
    let sessionStartYear = currentMonth >= 3 ? currentYear : currentYear - 1;
    
    // Session starts from March of sessionStartYear
    const startDate = new Date(sessionStartYear, 2, 1); // Month 2 = March (0-indexed)
    
    const { data, error } = await supabase
      .from('reconciliation')
      .select('year, month, balance, expenditure')
      .gte('year', sessionStartYear)
      .order('year', { ascending: true })
      .order('month', { ascending: true });
    
    if (error) throw error;
    
    // Group by month and calculate totals
    const monthlyData = {};
    data.forEach(record => {
      const monthKey = `${record.year}-${String(record.month).padStart(2, '0')}`;
      
      // Get total students from balance['G. Total']['No. St.']
      const totalStudents = record.balance?.['G. Total']?.['No. St.'] || 0;
      
      // Get total income as sum of all values in _income
      let totalIncome = 0;
      if (record.balance?._income) {
        Object.values(record.balance._income).forEach(amount => {
          totalIncome += parseFloat(amount) || 0;
        });
      }
      
      // Calculate total expenditure (excluding 'Salary to Staffs' and 'distribution')
      let totalExpenditure = 0;
      if (record.expenditure) {
        Object.entries(record.expenditure).forEach(([label, amount]) => {
          if (label !== 'Salary to Staffs' && label !== 'distribution') {
            totalExpenditure += parseFloat(amount) || 0;
          }
        });
      }
      
      monthlyData[monthKey] = {
        revenue: totalIncome,
        expenditure: totalExpenditure,
        students: totalStudents
      };
    });
    
    return monthlyData;
  } catch (error) {
    console.error('Error fetching reconciliation history:', error);
    throw error;
  }
};

export const fetchClassWiseFeeCollection = async (month = null, year = null, activeFilter = 'all') => {
  try {
    // If no month/year provided, use current month
    const today = new Date();
    const targetYear = year || today.getFullYear();
    const targetMonth = month || today.getMonth() + 1;
    
    const firstDayOfMonth = new Date(targetYear, targetMonth - 1, 1);
    const lastDayOfMonth = new Date(targetYear, targetMonth, 0, 23, 59, 59);
    
    // Fetch transactions for the specified month with student details
    let query = supabase
      .from('payment_transactions')
      .select(`
        amount_paid,
        student_rno,
        students!inner (Class, Active)
      `)
      .gte('created_at', firstDayOfMonth.toISOString())
      .lte('created_at', lastDayOfMonth.toISOString());
    
    if (activeFilter === 'active') query = query.eq('students.Active', true);
    else if (activeFilter === 'inactive') query = query.eq('students.Active', false);
    
    const { data, error } = await query;
    
    if (error) throw error;
    
    // Group by class
    const classWiseCollection = {};
    data.forEach(transaction => {
      const studentClass = transaction.students?.Class || 'Unknown';
      classWiseCollection[studentClass] = (classWiseCollection[studentClass] || 0) + parseFloat(transaction.amount_paid || 0);
    });
    
    return classWiseCollection;
  } catch (error) {
    console.error('Error fetching class-wise collection:', error);
    throw error;
  }
};

export const fetchDailyCollectionForMonth = async (month = null, year = null, activeFilter = 'all') => {
  try {
    const today = new Date();
    const targetYear = year || today.getFullYear();
    const targetMonth = month || today.getMonth() + 1;
    
    const firstDayOfMonth = new Date(targetYear, targetMonth - 1, 1);
    const lastDayOfMonth = new Date(targetYear, targetMonth, 0, 23, 59, 59);
    
    // Fetch all transactions for the month
    let query = supabase
      .from('payment_transactions')
      .select(`created_at, amount_paid, students!inner (Active)`)
      .gte('created_at', firstDayOfMonth.toISOString())
      .lte('created_at', lastDayOfMonth.toISOString())
      .order('created_at', { ascending: true });
    
    if (activeFilter === 'active') query = query.eq('students.Active', true);
    else if (activeFilter === 'inactive') query = query.eq('students.Active', false);
    
    const { data, error } = await query;
    
    if (error) throw error;
    
    // Group by day
    const dailyCollection = {};
    data.forEach(transaction => {
      const date = new Date(transaction.created_at);
      const dayKey = String(date.getDate()).padStart(2, '0');
      dailyCollection[dayKey] = (dailyCollection[dayKey] || 0) + parseFloat(transaction.amount_paid || 0);
    });
    
    // Create array with all days of the month
    const daysInMonth = new Date(targetYear, targetMonth, 0).getDate();
    const dailyData = [];
    
    for (let day = 1; day <= daysInMonth; day++) {
      const dayKey = String(day).padStart(2, '0');
      dailyData.push({
        day: day,
        date: `${targetYear}-${String(targetMonth).padStart(2, '0')}-${dayKey}`,
        collection: dailyCollection[dayKey] || 0
      });
    }
    
    return dailyData;
  } catch (error) {
    console.error('Error fetching daily collection:', error);
    throw error;
  }
};

export const fetchTopDefaulters = async (limit = 10, activeFilter = 'all') => {
  try {
    let query = supabase
      .from('students_private_details')
      .select(`
        Rno,
        FeeDues,
        students!inner (Name, Class, Active)
      `)
      .lt('FeeDues', 0)
      .order('FeeDues', { ascending: true })
      .limit(limit);
    
    if (activeFilter === 'active') query = query.eq('students.Active', true);
    else if (activeFilter === 'inactive') query = query.eq('students.Active', false);
    
    const { data, error } = await query;
    
    if (error) throw error;
    
    return data.map(student => ({
      rno: student.Rno,
      name: student.students?.Name || 'Unknown',
      class: student.students?.Class || 'Unknown',
      dues: Math.abs(parseFloat(student.FeeDues || 0))
    }));
  } catch (error) {
    console.error('Error fetching top defaulters:', error);
    throw error;
  }
};

export const fetchDashboardStudentStats = async () => {
  try {
    // Fetch all students
    const { data: studentsData, error } = await supabase
      .from('students')
      .select('Class, Gender, Active, Transport');
    
    if (error) throw error;
    
    const totalStudents = studentsData.length;
    const activeStudents = studentsData.filter(s => s.Active).length;
    const inactiveStudents = totalStudents - activeStudents;
    
    // Class-wise distribution
    const classDistribution = {};
    studentsData.forEach(student => {
      const cls = student.Class || 'Unknown';
      classDistribution[cls] = (classDistribution[cls] || 0) + 1;
    });
    
    // Gender distribution
    const genderDistribution = {
      Male: studentsData.filter(s => s.Gender === 'Male').length,
      Female: studentsData.filter(s => s.Gender === 'Female').length
    };
    
    // Transport users
    const transportUsers = studentsData.filter(s => s.Transport === true || s.Transport === 'Yes').length;
    
    return {
      totalStudents,
      activeStudents,
      inactiveStudents,
      classDistribution,
      genderDistribution,
      transportUsers
    };
  } catch (error) {
    console.error('Error fetching student stats:', error);
    throw error;
  }
};

export const fetchNewAdmissions = async (days = 30) => {
  try {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);
    
    const { data, error } = await supabase
      .from('students_private_details')
      .select(`
        Rno,
        "Date of Admission",
        students (Name, Class)
      `)
      .gte('Date of Admission', startDate.toISOString().split('T')[0])
      .order('Date of Admission', { ascending: false });
    
    if (error) throw error;
    
    return data.map(student => ({
      rno: student.Rno,
      name: student.students?.Name || 'Unknown',
      class: student.students?.Class || 'Unknown',
      admissionDate: student['Date of Admission']
    }));
  } catch (error) {
    console.error('Error fetching new admissions:', error);
    throw error;
  }
};

export const fetchTodayAttendance = async () => {
  try {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth() + 1;
    const day = today.getDate();
    const dayKey = String(day).padStart(2, '0'); // Format as "01", "02", etc.
    
    // Fetch all active students
    const { data: studentsData, error: studentsError } = await supabase
      .from('students')
      .select('Rno, Name, Class')
      .eq('Active', true);
    
    if (studentsError) throw studentsError;
    
    // Fetch attendance for students for this month/year (records with Rno)
    const { data: attendanceData, error: attendanceError } = await supabase
      .from('attendance')
      .select('*')
      .not('Rno', 'is', null)
      .eq('month', month)
      .eq('year', year);
    
    if (attendanceError) throw attendanceError;
    
    let presentCount = 0;
    const attendanceMap = {};
    
    attendanceData?.forEach(record => {
      attendanceMap[record.Rno] = record[dayKey];
      if (record[dayKey] === true) {
        presentCount++;
      }
    });
    
    const totalStudents = studentsData.length;
    const absentCount = totalStudents - presentCount;
    const attendanceRate = totalStudents > 0 ? (presentCount / totalStudents) * 100 : 0;
    
    return {
      totalStudents,
      presentCount,
      absentCount,
      attendanceRate: attendanceRate.toFixed(2)
    };
  } catch (error) {
    console.error('Error fetching today attendance:', error);
    throw error;
  }
};

export const fetchTodayTeacherAttendance = async () => {
  try {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth() + 1;
    const day = today.getDate();
    const dayKey = String(day).padStart(2, '0'); // Format as "01", "02", etc.
    
    // Fetch all active staffs
    const { data: staffsData, error: staffsError } = await supabase
      .from('staffs')
      .select('staff_no, name')
      .eq('is_active', true);
    
    if (staffsError) throw staffsError;
    
    // Fetch today's attendance for staffs (records with Tno)
    const { data: attendanceData, error: attendanceError } = await supabase
      .from('attendance')
      .select('*')
      .not('Tno', 'is', null)
      .eq('month', month)
      .eq('year', year);
    
    if (attendanceError) throw attendanceError;
    
    let presentCount = 0;
    attendanceData?.forEach(record => {
      if (record[dayKey] === true) {
        presentCount++;
      }
    });
    
    const totalStaffs = staffsData.length;
    const absentCount = totalStaffs - presentCount;
    const attendanceRate = totalStaffs > 0 ? (presentCount / totalStaffs) * 100 : 0;
    
    return {
      totalStaffs,
      presentCount,
      absentCount,
      attendanceRate: attendanceRate.toFixed(2)
    };
  } catch (error) {
    console.error('Error fetching staff attendance:', error);
    throw error;
  }
};

export const fetchAttendanceTrend = async (days = 7) => {
  try {
    const today = new Date();
    const trendData = [];
    
    // Fetch all active students once
    const { data: studentsData } = await supabase
      .from('students')
      .select('Rno')
      .eq('Active', true);
    
    if (!studentsData) return [];
    const totalStudents = studentsData.length;
    
    // Calculate date range
    const startDate = new Date(today);
    startDate.setDate(startDate.getDate() - days + 1);
    
    // Get unique months we need to fetch
    const monthsToFetch = new Set();
    for (let i = 0; i < days; i++) {
      const date = new Date(startDate);
      date.setDate(date.getDate() + i);
      monthsToFetch.add(`${date.getFullYear()}-${date.getMonth() + 1}`);
    }
    
    // Fetch attendance data for all required months
    const attendanceByMonth = {};
    for (const monthKey of monthsToFetch) {
      const [year, month] = monthKey.split('-').map(Number);
      const { data } = await supabase
        .from('attendance')
        .select('*')
        .not('Rno', 'is', null)
        .eq('month', month)
        .eq('year', year);
      
      if (data) {
        attendanceByMonth[monthKey] = data;
      }
    }
    
    // Calculate attendance for each day
    for (let i = 0; i < days; i++) {
      const date = new Date(startDate);
      date.setDate(date.getDate() + i);
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const day = date.getDate();
      const dayKey = String(day).padStart(2, '0');
      const monthKey = `${year}-${month}`;
      
      // Count present students for this day
      let presentCount = 0;
      const attendanceData = attendanceByMonth[monthKey] || [];
      attendanceData.forEach(record => {
        if (record[dayKey] === true) {
          presentCount++;
        }
      });
      
      const rate = totalStudents > 0 ? (presentCount / totalStudents) * 100 : 0;
      
      trendData.push({
        date: date.toISOString().split('T')[0],
        rate: rate.toFixed(2),
        present: presentCount,
        total: totalStudents
      });
    }
    
    return trendData;
  } catch (error) {
    console.error('Error fetching attendance trend:', error);
    throw error;
  }
};

export const fetchLowAttendanceStudents = async (threshold = 75) => {
  try {
    const today = new Date();
    const month = today.getMonth() + 1;
    const year = today.getFullYear();
    const currentDay = today.getDate();
    
    // Fetch all active students
    const { data: studentsData, error: studentsError } = await supabase
      .from('students')
      .select('Rno, Name, Class')
      .eq('Active', true);
    
    if (studentsError) throw studentsError;
    
    // Fetch attendance for all students this month
    const { data: attendanceData, error: attendanceError } = await supabase
      .from('attendance')
      .select('*')
      .not('Rno', 'is', null)
      .eq('month', month)
      .eq('year', year);
    
    if (attendanceError) throw attendanceError;
    
    const attendanceMap = {};
    attendanceData?.forEach(record => {
      attendanceMap[record.Rno] = record;
    });
    
    // Calculate attendance percentage for each student
    const lowAttendanceStudents = [];
    studentsData.forEach(student => {
      const attendanceRecord = attendanceMap[student.Rno];
      if (!attendanceRecord) return;
      
      let presentDays = 0;
      for (let day = 1; day <= currentDay; day++) {
        const dayKey = String(day).padStart(2, '0');
        if (attendanceRecord[dayKey] === true) {
          presentDays++;
        }
      }
      
      const attendancePercentage = currentDay > 0 ? (presentDays / currentDay) * 100 : 0;
      
      if (attendancePercentage < threshold) {
        lowAttendanceStudents.push({
          rno: student.Rno,
          name: student.Name,
          class: student.Class,
          presentDays,
          totalDays: currentDay,
          attendancePercentage: attendancePercentage.toFixed(2)
        });
      }
    });
    
    // Sort by attendance percentage (lowest first)
    lowAttendanceStudents.sort((a, b) => a.attendancePercentage - b.attendancePercentage);
    
    return lowAttendanceStudents;
  } catch (error) {
    console.error('Error fetching low attendance students:', error);
    throw error;
  }
};
